import { api } from './api.ts';
import { appNotify, appRequest, isApp } from './app-bridge.ts';
import { cipher } from './session.ts';
import type { DaoApiResponse } from '@/types/dao.ts';

/**
 * 앱 푸시. (2026-10-08)
 *
 * 앱이 FCM 토큰을 받아 오면 **여기서** 서버에 등록한다. 로그인 토큰(`Authorization`)과
 * 본문 암호화가 웹 쪽에 있어서, 앱이 직접 등록하지 않고 이 길을 탄다.
 * 서버는 토큰의 주인(로그인 회원)을 기기에 붙인다 — 로그아웃 상태로 다시 등록하면
 * 회원이 떨어져 회원 대상 푸시가 그 기기로 가지 않는다.
 *
 * 일반 브라우저에서는 아무것도 하지 않는다.
 *
 * ★ 언제 다시 등록하나: 앱을 켤 때, 로그인·로그아웃할 때, 앱이 `pushToken` 이벤트로
 *   토큰이 바뀌었다고 알릴 때 (`app.tsx`).
 */

interface AppPushToken {
  available: boolean;
  token?: string;
  oldToken?: string | null;
  notiEnabled?: boolean;
  platform?: string;
  appVersion?: string;
}

export interface PushState {
  /** 이 기기에서 알림 받기 (서버 값) */
  pushYn: 'Y' | 'N';
  /** 안드로이드 설정에서 알림이 켜져 있나 */
  notiEnabled: boolean;
}

/** 마지막으로 등록한 토큰. 설정을 바꿀 때 쓴다. */
let currentToken: string | null = null;

/**
 * 서버에 이 기기를 등록한다. 앱이 아니거나 푸시 설정이 안 된 앱이면 null.
 *
 * 실패해도 던지지 않는다. 푸시 등록 실패로 화면이 멈추면 안 된다.
 */
export async function syncPush(): Promise<PushState | null> {
  if (!isApp()) return null;

  try {
    const info = await appRequest<AppPushToken>('pushToken');
    if (!info.available || !info.token) return null;

    const encrypted = await cipher.encryptJson({
      token: info.token,
      oldToken: info.oldToken ?? undefined,
      notiEnabled: info.notiEnabled ?? true,
      platform: info.platform ?? 'ANDROID',
      appVersion: info.appVersion ?? '',
    });
    const response = await api.post<DaoApiResponse<{ pushYn: 'Y' | 'N' }>>(
      '/api/push/register.json',
      encrypted
    );
    if (!response.result) return null;

    currentToken = info.token;
    return { pushYn: response.data?.pushYn ?? 'Y', notiEnabled: info.notiEnabled ?? true };
  } catch {
    return null;
  }
}

/** 이 기기에서 알림 받기 켜기/끄기 */
export async function setPushEnabled(on: boolean): Promise<boolean> {
  if (!currentToken) await syncPush();
  if (!currentToken) return false;

  try {
    const encrypted = await cipher.encryptJson({ token: currentToken, pushYn: on ? 'Y' : 'N' });
    const response = await api.post<DaoApiResponse<{ pushYn: string }>>(
      '/api/push/setting.json',
      encrypted
    );
    return !!response.result;
  } catch {
    return false;
  }
}

/**
 * 알림 권한을 묻는다 (Android 13+). 이미 허락했거나 그 아래 버전이면 바로 답이 온다.
 * 돌려주는 값은 '지금 알림이 보이는가'.
 */
export async function requestPushPermission(): Promise<boolean> {
  if (!isApp()) return false;
  try {
    const { notiEnabled } = await appRequest<{ notiEnabled: boolean }>('pushPermission');
    return notiEnabled;
  } catch {
    return false;
  }
}

/** 안드로이드의 이 앱 알림 설정 화면을 연다. 권한을 '다시 묻지 않음' 으로 거절한 사람용. */
export function openNotificationSettings(): void {
  appNotify('notificationSettings');
}
